
import { appDebugLog } from '@/utils/appDebugLog';
/**
 * GeoMoverDB - IndexedDB wrapper for the Geo Mover application
 *
 * This module provides a Promise-based API for all database operations.
 * It manages multiple object stores: settings, activityTypes, trackedRoutes,
 * trackedRouteVersions, notes, tags, poiLists, poiTaxonomies, pois,
 * poiNoteLinks, poiRouteLinks, poiPhotos, poiSearchRadiusPresets, mapHosts, overlayServices,
 * overlayState, settingsPresets, colorPalettes, routingProviders.
 *
 * @module GeoMoverDB
 * @see https://dev.to/andyhaskell/build-a-basic-web-app-with-geoMoverDB-38ef
 *
 * @example
 * // Initialize the database
 * import GeoMoverDB from '@/db/geoMoverDB'
 * await GeoMoverDB.init()
 *
 * // CRUD operations
 * const id = await GeoMoverDB.addOne('trackedRoutes', routeData)
 * const route = await GeoMoverDB.findOne('trackedRoutes', id)
 * await GeoMoverDB.updateOne('trackedRoutes', { title: 'New Title' }, id)
 * await GeoMoverDB.deleteOne('trackedRoutes', id)
 */
// Initial data imports for fresh database creation
import initialSettingsData from './data/initialSettingsData';
import initialActivityTypesData from './data/initialActivityTypesData';
import initialTrackedRoutesData from './data/initialTrackedRoutesData';
import initialNotesData from './data/initialNotesData';
import { mapHostsData as initialMapHostsData } from './data/mapHostsData';
import { overlayServicesData as initialOverlayServicesData } from './data/overlayServicesData';
import { routingProvidersData as initialRoutingProvidersData } from './data/routingProvidersData';
import initialSettingsPresetsData from './data/initialSettingsPresetsData';
import initialColorPalettesData from './data/initialColorPalettesData';
import initialTagsData from './data/initialTagsData';
import initialPoiListsData from './data/initialPoiListsData';
import initialPoiTaxonomiesData from './data/initialPoiTaxonomiesData';
import initialPoiSearchRadiusPresetsData from './data/initialPoiSearchRadiusPresetsData';
import initialIconCatalogData from './data/initialIconCatalogData';
// Utility for re-adding preset data (used for reset functionality)
import addStorePresets from './reAddStoreData';


// TODO: Add table for: activities, Keys for map APIs (esp reverse coding)
let GeoMoverDB = {};

/**
 * Current database version. Increment when schema changes are needed.
 * Version 2: Added tags store and tagIds multiEntry index on trackedRoutes
 * Version 3: Added routingProviders store for BYOK track-correction providers
 * Version 4: Added ArcGIS / Esri map provider to mapHosts
 * Version 5: Added trackedRouteVersions store for track revision history
 * Version 6: Keep compatibility with existing user databases at schema v6
 * Version 7: Added POI stores and relations (lists, taxonomies, POIs, links, photos)
 * Version 8: Renamed POI-note link store to poiNoteLinks (migration-safe)
 * Version 9: Added noteRouteLinks store for notes <-> trackedRoutes relations
 * Version 10: Added poiSearchRadiusPresets store for editable POI search radius values
 * Version 11: Added iconCatalog store for shared Activity Type / POI Taxonomy icon sets
 * @type {number}
 */
const version = 11;
const DEFAULT_POI_LIST_TITLE = 'Default POIs';
const MAP_HOST_GEOCODE_CAPABILITY_PRESETS = {
  nominatim: {
    supportsForwardGeocode: true,
    supportsReverseGeocode: true,
    supportsRadiusBias: true,
    requiresApiKey: false,
    isEnabled: true
  },
  here: {
    supportsForwardGeocode: true,
    supportsReverseGeocode: true,
    supportsRadiusBias: true,
    requiresApiKey: true,
    isEnabled: false
  },
  mapbox: {
    supportsForwardGeocode: true,
    supportsReverseGeocode: true,
    supportsRadiusBias: true,
    requiresApiKey: true,
    isEnabled: false
  },
  azure: {
    supportsForwardGeocode: true,
    supportsReverseGeocode: true,
    supportsRadiusBias: true,
    requiresApiKey: true,
    isEnabled: false
  },
  arcgis: {
    supportsForwardGeocode: true,
    supportsReverseGeocode: true,
    supportsRadiusBias: true,
    requiresApiKey: true,
    isEnabled: false
  }
};
const MAP_HOST_GEOCODE_CAPABILITY_KEYS = [
  'supportsForwardGeocode',
  'supportsReverseGeocode',
  'supportsRadiusBias',
  'requiresApiKey',
  'isEnabled',
  'geosearchPolicyExplicit'
];

function cloneIndexedDbPayload (value) {
  try {
    return structuredClone(value);
  }
  catch {
    return JSON.parse(JSON.stringify(value));
  }
}

function createDefaultPoiListRecord () {
  return {
    title: DEFAULT_POI_LIST_TITLE,
    isDefault: true,
    createdAt: Date.now(),
    updatedAt: null
  };
}

function ensureDefaultPoiListSeed (poiListsStore) {
  if (!poiListsStore) return;

  const addDefaultIfMissing = () => {
    const addReq = poiListsStore.add(createDefaultPoiListRecord());
    addReq.onsuccess = () => {
      appDebugLog.info('GeoMoverDB', `Seeded mandatory POI list: "${DEFAULT_POI_LIST_TITLE}"`);
    };
    addReq.onerror = (event) => {
      // Non-fatal: default list may already exist from parallel migration branches/states.
      event.preventDefault?.();
      appDebugLog.warn('GeoMoverDB', `Could not seed default POI list "${DEFAULT_POI_LIST_TITLE}" (already exists or key conflict).`);
    };
  };

  const cursorReq = poiListsStore.openCursor();
  cursorReq.onsuccess = () => {
    const cursor = cursorReq.result;
    if (!cursor) {
      addDefaultIfMissing();
      return;
    }

    if (cursor.value?.isDefault === true) {
      return;
    }

    cursor.continue();
  };
  cursorReq.onerror = () => {
    appDebugLog.warn('GeoMoverDB', 'Could not scan poiLists store during default-list seed check.');
  };
}

function applyMapHostGeocodeCapabilityDefaults (mapHost) {
  const preset = MAP_HOST_GEOCODE_CAPABILITY_PRESETS[mapHost?.slug] || {};
  const geosearchPolicyExplicit = Boolean(mapHost?.geosearchPolicyExplicit);

  const resolveIsEnabled = () => {
    if (typeof mapHost?.isEnabled === 'boolean') {
      // Legacy DB states can carry a derived false for Nominatim.
      // Keep Nominatim default-enabled unless user explicitly changed policy.
      if (mapHost?.slug === 'nominatim' && mapHost.isEnabled === false && !geosearchPolicyExplicit) {
        return true;
      }
      return mapHost.isEnabled;
    }

    if (mapHost?.slug === 'nominatim') {
      return true;
    }

    if (typeof mapHost?.activatedForGeocoding === 'boolean') {
      return mapHost.activatedForGeocoding;
    }

    if (typeof mapHost?.active === 'boolean') {
      return mapHost.active;
    }

    if (typeof preset.isEnabled === 'boolean') {
      return preset.isEnabled;
    }

    return false;
  };

  return {
    supportsForwardGeocode: mapHost?.supportsForwardGeocode
      ?? preset.supportsForwardGeocode
      ?? Boolean(mapHost?.canGeocode),
    supportsReverseGeocode: mapHost?.supportsReverseGeocode
      ?? preset.supportsReverseGeocode
      ?? Boolean(mapHost?.canGeocode),
    supportsRadiusBias: mapHost?.supportsRadiusBias
      ?? preset.supportsRadiusBias
      ?? false,
    requiresApiKey: mapHost?.requiresApiKey
      ?? preset.requiresApiKey
      ?? Boolean(mapHost?.apikey?.required),
    isEnabled: resolveIsEnabled(),
    geosearchPolicyExplicit
  };
}

GeoMoverDB = {
  /**
   * The IDBDatabase instance after successful init()
   * @type {IDBDatabase|null}
   */
  db: null,

  /**
   * The IDBOpenDBRequest from indexedDB.open()
   * @type {IDBOpenDBRequest|null}
   */
  openDBRequest: null,

  /**
   * CRUD methods to be used with the "settings" store ONLY!!!
   *
   * @param {*} obj Object that holds 2 params:
   *    [string] feature (any of "enableHighAccuracy","timeout","maximumAges","addressService")
   *    [bool|string] value (for toggles like "enableHighAccuracy" itis a boolean, others are strings)
   *
   * @example
   * const updatedSettingsData = GeoMoverDB.updateOneSetting({
   *   feature: 'enableHighAccuracy',
   *   value: bool
   * })
   */
  updateOneSetting (obj) {
    // appDebugLog.log('GEOMOVERDB::updateOneSetting this.db =', this.db)
    // appDebugLog.log('GEOMOVERDB::updateOneSetting obj =', obj)

    return new Promise((resolve, reject) => {
      // Start a database transaction and get the notes object store
      const tx = this.db.transaction(['settings'], 'readwrite');
      const settingsStore = tx.objectStore('settings');
      const cursorRequest = settingsStore.openCursor();

      cursorRequest.onsuccess = e => {
        const cursor = e.target.result;
        // appDebugLog.log('GEOMOVERDB::updateOneSetting:cursorRequest cursor =', cursor)

        if (cursor) {
          const itemToUpdate = cursor.value;
          // appDebugLog.log('GEOMOVERDB::updateOneSetting:cursorRequest itemToUpdate =', itemToUpdate)
          itemToUpdate[obj.feature] = obj.value;
          itemToUpdate.lastUpdated = Date.now();

          const updateRequest = cursor.update(itemToUpdate);

          updateRequest.onsuccess = e => {
            resolve(true);
          };

          updateRequest.onerror = e => {
            // appDebugLog.log('Update error happened: e =', e)
            reject(new Error(e));
          };
        }
      };

      cursorRequest.onerror = e => {
        appDebugLog.log('cursorRequestError =', e);
      };
    });
  },

  /**
   * Get a single setting value from the settings store
   *
   * @param {string} which - The setting key to retrieve (e.g., 'enableHighAccuracy', 'timeout')
   * @returns {Promise<*>} The value of the requested setting
   * @example
   * const accuracy = await GeoMoverDB.getOneSetting('enableHighAccuracy')
   */
  getOneSetting (which) {
    // appDebugLog.log('GEOMOVERDB::getOneSetting which =', which)
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('settings', 'readonly');
      const settingsStore = tx.objectStore('settings');
      const cursorRequest = settingsStore.openCursor();

      cursorRequest.onsuccess = e => {
        const cursor = e.target.result;

        if (cursor) {
          // cursor.value
        }
      };
    });
  },

  /**
   * Retrieve all settings from the settings store
   *
   * @returns {Promise<Object>} Object containing all settings key-value pairs
   * @example
   * const settings = await GeoMoverDB.getAllSettings()
   * appDebugLog.log(settings.enableHighAccuracy, settings.timeout)
   */
  getAllSettings () {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('settings', 'readonly');
      const settingsStore = tx.objectStore('settings');
      const cursorRequest = settingsStore.openCursor();

      cursorRequest.onsuccess = e => {
        const cursor = e.target.result;

        if (cursor) {
          resolve(cursor.value);
        }
      };

      cursorRequest.onerror = e => {
        reject(e);
      };
    });
  },

  /*******************************************
   * CRUD Methods for all stores (not settings)
   *******************************************/

  /**
   * Add a single record to an object store
   *
   * @param {string} store - The object store name ('trackedRoutes', 'notes', 'activityTypes', etc.)
   * @param {Object} data - The data object to insert (id will be auto-generated)
   * @returns {Promise<number>} The auto-generated ID of the new record
   * @throws {Error} If the transaction fails
   * @example
   * const newRouteId = await GeoMoverDB.addOne('trackedRoutes', {
   *   title: 'Morning Run',
   *   points: [[lat, lon, time], ...],
   *   created: Date.now()
   * })
   */
  addOne (store, data) {
    return new Promise((resolve, reject) => {
      let addOperationStatus = false;
      let errorMsg = '';
      let newActivityId = null;

      // Start a database transaction and get the notes object store
      const tx = this.db.transaction([store], 'readwrite');
      const objStore = tx.objectStore(store);

      // Add the data to the object store (or put)
      // A request returns an onsuccess handler, from there we get the
      // newly created id, which we neewd for the vuex store-saved instance
      const req = objStore.add(data);
      req.onsuccess = e => {
        // appDebugLog.log('req success! e =', e.target)
        addOperationStatus = true;
        newActivityId = e.target.result;
      };

      req.onerror = error => {
        addOperationStatus = false;
        errorMsg = error;
        // appDebugLog.log('req error =', error)
      };

      // Wait for the database transaction to complete
      // A transaction has an oncomplete event, but NOT an onsuccess! (==> see request)
      tx.oncomplete = result => {
        // appDebugLog.log('IDB addOne: return success from oncomplete. result =', result)
        if (addOperationStatus) {
          resolve(newActivityId);
        }
        else {
          resolve(errorMsg);
        }
      };

      tx.onerror = e => {
        // appDebugLog.log('IDB addOne: return error from onerror:', e)
        reject(e);
      };
    });
  },

  /**
   * Batch insert multiple records at once (much faster than individual addOne calls)
   *
   * @param {string} store - The object store name
   * @param {Array} dataArray - Array of records to insert
   * @returns {Promise<Object>} - Result { success: boolean, inserted: number, ids: number[], errors: string[] }
   */
  addMany (store, dataArray) {
    return new Promise((resolve, reject) => {
      if (!Array.isArray(dataArray) || dataArray.length === 0) {
        resolve({ success: true, inserted: 0, ids: [], errors: [] });
        return;
      }

      const result = {
        success: true,
        inserted: 0,
        ids: [],
        errors: []
      };

      // Use a single transaction for all inserts (much faster)
      const tx = this.db.transaction([store], 'readwrite');
      const objStore = tx.objectStore(store);

      // Track pending operations
      let pendingCount = dataArray.length;

      dataArray.forEach((data, index) => {
        const req = objStore.add(data);

        req.onsuccess = e => {
          result.inserted++;
          result.ids.push(e.target.result);
          pendingCount--;
        };

        req.onerror = e => {
          result.errors.push(`Record ${index}: ${e.target.error?.message || 'Unknown error'}`);
          result.success = false;
          pendingCount--;
          // Don't reject - continue with other inserts
          e.preventDefault(); // Prevent transaction abort
        };
      });

      tx.oncomplete = () => {
        resolve(result);
      };

      tx.onerror = e => {
        result.success = false;
        result.errors.push(`Transaction error: ${e.target.error?.message || 'Unknown error'}`);
        resolve(result); // Still resolve with partial results
      };

      tx.onabort = e => {
        result.success = false;
        result.errors.push('Transaction aborted');
        resolve(result);
      };
    });
  },

  /**
   * Update an existing record by merging new data with existing data
   *
   * @param {string} store - The object store name
   * @param {Object} newData - Object containing the fields to update (merged with existing)
   * @param {number} key - The ID of the record to update
   * @returns {Promise<boolean>} True if update successful
   * @throws {Error} If record not found or newData is empty
   * @example
   * await GeoMoverDB.updateOne('trackedRoutes', { title: 'Updated Title' }, 42)
   */
  updateOne (store, newData, key) {
    // appDebugLog.log('GeoMoverDB: updateOne data =', newData)
    // appDebugLog.log('> store =', store)
    // appDebugLog.log('> newData =', newData)
    // appDebugLog.log('> key =', key)
    // appDebugLog.log(' ')

    return new Promise((resolve, reject) => {
      if (newData) {
        const tx = this.db.transaction([store], 'readwrite');
        const objStore = tx.objectStore(store);
        const req = objStore.get(key);

        // First get existing item...
        req.onsuccess = e => {
          // Fetch existing data
          const currentData = e.target.result;

          // add new data, save to new array
          const newArray = cloneIndexedDbPayload(Object.assign({}, currentData, newData));

          // Second update existing item...
          const updateRequest = objStore.put(newArray);

          updateRequest.onsuccess = () => {
            resolve(true);
          };
          updateRequest.onerrror = err => {
            reject(new Error(err));
          };
        };
        req.onerror = err => {
          reject(new Error('Could not open the DB on updateOne, error =', err));
        };
      }
      else {
        reject(new Error('GeoMoverDB: updateOne - No newData or key given. Did you select an item? Is the key a number (correct) or string (wrong)?'));
      }
    });
  },

  /**
   * Delete a single record from an object store by ID
   *
   * @param {string} store - The object store name
   * @param {number} itemid - The ID of the record to delete
   * @returns {Promise<Event>} The success event from the delete operation
   * @throws {Error} If record not found or deletion fails
   * @example
   * await GeoMoverDB.deleteOne('trackedRoutes', 42)
   */
  deleteOne (store, itemid) {
    // appDebugLog.log('itemid =', itemid)

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([store], 'readwrite');
      const objStore = tx.objectStore(store);
      const req = objStore.openCursor();

      req.onsuccess = e => {
        const cursor = e.target.result;

        if (cursor) {
          if (cursor.value.id === itemid) {
            // appDebugLog.log('Found specific entry with id ', itemid)
            const deleteRequest = cursor.delete(itemid);

            deleteRequest.onsuccess = e => {
              // appDebugLog.log('Deleted entry with id', itemid)
              // appDebugLog.log('e =', e)
              resolve(e);
            };

            deleteRequest.onerror = e => {
              // appDebugLog.log('There was an error deleting entry with id', itemid)
              reject(e);
            };
          }
          else {
            // didn't find proper result, continue searching
            cursor.continue();
          }
        }
      };

      req.onerror = e => {
        reject(e);
      };
    });
  },

  /**
   * Delete a specific key from a record (removes property, keeps record)
   *
   * @param {string} store - The object store name
   * @param {number} itemid - The ID of the record to modify
   * @param {string} key - The property key to delete from the record
   * @returns {Promise<boolean>} True if successful
   * @example
   * // Remove the 'notes' field from route 42
   * await GeoMoverDB.deleteOneKey('trackedRoutes', 42, 'notes')
   */
  deleteOneKey (store, itemid, key) {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([store], 'readwrite');
      const objStore = tx.objectStore(store);
      const req = objStore.get(itemid);

      req.onsuccess = e => {
        // Fetch existing data
        const currentData = e.target.result;
        // delete key
        delete currentData[key];

        // Update existing item
        const updateRequest = objStore.put(currentData);
        updateRequest.onsuccess = () => {
          resolve(true);
        };
        updateRequest.onerrror = err => {
          reject(new Error(err));
        };
      };

      req.onerror = e => {
        reject(e);
      };
    });
  },

  /**
   * Find a single record by ID
   *
   * @param {string} store - The object store name
   * @param {number|string} id - The record ID (will be parsed as integer)
   * @returns {Promise<Object>} The found record
   * @throws {Error} If id is null/undefined or record not found
   * @example
   * const route = await GeoMoverDB.findOne('trackedRoutes', 42)
   * appDebugLog.log(route.title, route.points)
   */
  findOne (store, id) {
    return new Promise((resolve, reject) => {
      // Validate id parameter
      if (id === undefined || id === null) {
        reject(new Error(`GeoMoverDB's "findOne" on store "${store}" requires a valid id, got: ${id}`));
        return;
      }

      const tx = this.db.transaction([store], 'readonly');
      const objStore = tx.objectStore(store);
      const req = objStore.get(parseInt(id)); // id must be cast to number!

      req.onsuccess = e => {
        const result = req.result;
        // Validate result is not undefined
        if (result === undefined) {
          reject(new Error(`GeoMoverDB's "findOne" on store "${store}" with id ${id} returned no result (record not found)`));
        }
        else {
          resolve(result);
        }
      };

      req.onerror = e => {
        reject(new Error('GeoMoverDB\'s "findOne" on store ' + store + ' with id ' + id + ' encountered an error: ' + (e.target.error?.message || 'Unknown error')));
      };
    });
  },

  // replaceAll (store, data) {
  //   return new Promise((resolve, reject) => {
  //     const tx = this.db.transaction([store], 'readwrite')
  //     const objStore = tx.objectStore(store)

  //     for (const datum in data) {
  //       const req = objStore.put(datum) // id must be cast to number!

  //       req.onsuccess = e => {
  //         appDebugLog.log('reaplceAll success: e =', e)
  //         resolve(req.result)
  //       }

  //       req.onerror = e => {
  //         reject(new Error('GeoMoverDB\'s "replaceAll" on store ' + store + ' width data ' + data + ' was not successful.'))
  //       }
  //     }
  //   })
  // },

  /**
   * Find records by a specific key value
   *
   * @param {string} store - The object store name
   * @param {*} key - The key value to search for
   * @returns {Promise<Object>} The matching record(s)
   * @example
   * // Find all active map hosts
   * const activeHosts = await GeoMoverDB.findManyByKey('mapHosts', 'active')
   */
  findManyByKey (store, key) {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([store], 'readonly');
      const objStore = tx.objectStore(store);
      const req = objStore.get(key);

      req.onsuccess = e => {
        resolve(req.result);
      };

      req.onerror = e => {
        reject(new Error('GeoMoverDB\'s "findManyByKey" on store ' + store + ' width key ' + key + ' did not yield a result'));
      };
    });
  },

  /**
   * Retrieve all records from an object store
   *
   * @param {string} store - The object store name
   * @param {string|null} [index=null] - Optional index name to sort by (e.g., 'timestamp')
   * @param {string} [dir='asc'] - Sort direction: 'asc' (ascending) or 'desc' (descending)
   * @returns {Promise<Array<Object>>} Array of all records in the store
   * @example
   * // Get all routes sorted by timestamp descending
   * const routes = await GeoMoverDB.findAll('trackedRoutes', 'timestamp', 'desc')
   */
  findAll (store, index = null, dir = 'asc') {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        appDebugLog.warn('GeoMoverDB', `findAll("${store}") called before DB initialization.`);
        resolve([]);
        return;
      }

      const tx = this.db.transaction([store], 'readonly');
      const objStore = tx.objectStore(store);

      const allEntries = [];
      let index = null;
      let req = null;
      let sortingDirection = null;

      if (dir === 'asc') {
        sortingDirection = 'next';
      }
      else {
        sortingDirection = 'prev';
      }

      // Create Cursor Request depending on whether we have an index to sort after or not
      if (index === null) {
        req = objStore.openCursor(null, sortingDirection);
      }
      else {
        index = objStore.index('timestamp');
        req = index.openCursor(null, sortingDirection);
      }

      req.onsuccess = e => {
        // The result of req.onsuccess in openCursor requests is an IDBCursor
        const cursor = e.target.result;

        if (cursor !== null) {
          allEntries.push(cursor.value);
          // jumps back to onsuccess
          cursor.continue();
        }
        else {
          resolve(allEntries);
        }
      };

      req.onerror = e => {
        reject(new Error(e));
      };
    });
  },

  /**
   * Retrieve paginated records from an object store
   *
   * @param {string} store - The object store name
   * @param {string|null} [indexOfStore=null] - Optional index name to sort by
   * @param {string} [dir='asc'] - Sort direction: 'asc' or 'desc'
   * @param {number} [limit=5] - Maximum number of records to return per page
   * @param {number} [offset=0] - Page number (0-based, multiplied by limit for cursor position)
   * @returns {Promise<Array<Object>>} Array of records for the requested page
   * @example
   * // Get page 2 (records 10-14) sorted by date descending
   * const page2 = await GeoMoverDB.findPaginated('trackedRoutes', 'date', 'desc', 5, 2)
   */
  async findPaginated (store, indexOfStore = null, dir = 'asc', limit = 5, offset = 0) {
    // console.groupCollapsed('findPaginated of store "' + store.toUpperCase() + '"')
    // appDebugLog.log('> indexOfStore =', indexOfStore)
    // appDebugLog.log('> dir =', dir)
    // appDebugLog.log('> limit =', limit)
    // appDebugLog.log('> offset =', offset)

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([store], 'readonly');
      const objStore = tx.objectStore(store);

      const foundEntries = [];
      let index = indexOfStore;
      let req = null;
      let sortingDirection = null;

      // Calculate inital offset
      const forwardCursorPosition = limit * offset;
      // Since there is a loop, when fetching more than one item, we
      // need to make sure, that "advance" is only called once
      let advanced = false;
      // Number of items we already got, must not be more then limit
      let counter = 0;

      if (dir === 'asc') {
        sortingDirection = 'next';
      }
      else {
        sortingDirection = 'prev';
      }
      // appDebugLog.log('> sortingDirection =', sortingDirection)

      // Create Cursor Request depending on whether we have an index to sort after or not
      if (index === null) {
        // appDebugLog.log('index = 0')
        req = objStore.openCursor(null, sortingDirection);
      }
      else {
        // appDebugLog.log('> index =', index)
        index = objStore.index(index);
        req = index.openCursor(null, sortingDirection);
      }

      req.onsuccess = e => {
        // The result of req.onsuccess in openCursor requests is an IDBCursor
        const cursor = e.target.result;

        // Forward cursor
        if (forwardCursorPosition !== 0 && !advanced) {
          cursor.advance(forwardCursorPosition);
          // Set advanced to true so this won't happen again in the current loop
          advanced = true;
          // And quit!!! Importnat!
          return;
        }

        if (cursor !== null && counter < limit) {
          counter++;
          foundEntries.push(cursor.value);
          // jumps back to onsuccess
          cursor.continue();
        }
        else {
          resolve(foundEntries);
        }
      };

      req.onerror = e => {
        reject(new Error(e));
      };

      // console.groupEnd()
    });
  },

  /**
   * Get the last (highest) auto-incremented ID in a store
   *
   * @param {string} store - The object store name
   * @returns {Promise<number>} The highest ID, or 0 if store is empty
   * @see https://googlechrome.github.io/samples/idb-getall/
   * @example
   * const lastId = await GeoMoverDB.getLastId('trackedRoutes')
   * appDebugLog.log('Next ID will be:', lastId + 1)
   */
  getLastId (store) {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([store], 'readonly');
      const objStore = tx.objectStore(store);
      const cursorRequest = objStore.openCursor(null, 'prev');

      cursorRequest.onsuccess = e => {
        // In case the DB was completely empty, the result is null
        if (e.target.result !== null && e.target.result.key) {
          resolve(e.target.result.key);
        }
        else {
          resolve(0);
        }
      };

      cursorRequest.onerror = e => {
        reject(new Error(e));
      };
    });
  },

  /*******************************************
   * Pagination Helpers
   *******************************************/

  /**
   * Get the total count of records in an object store
   *
   * @param {string} store - The object store name
   * @returns {Promise<number>} Total number of records in the store
   * @example
   * const total = await GeoMoverDB.getTotalCount('trackedRoutes')
   * const totalPages = Math.ceil(total / itemsPerPage)
   */
  async getTotalCount (store) {
    // appDebugLog.log('GeoMoverDB: getTotalcount - invoked; store =', store)
    return new Promise((resolve, reject) => {
      // appDebugLog.log('> Created promise')
      const tx = this.db.transaction([store], 'readonly');
      const objStore = tx.objectStore(store);
      const req = objStore.count();

      req.onsuccess = e => {
        // appDebugLog.log(`> Total entries in store >${store}<: ${e.target.result}`)
        resolve(e.target.result);
      };

      req.onerror = error => {
        reject(new Error(`> Could not retrieve a count on store ${store}. Error: ${error}`));
      };
    });
  },

  /*******************************************
   * Store Initialization / Reset
   *******************************************/

  /**
   * Re-add preset/default data to a store (useful for reset functionality)
   *
   * @param {string} store - The object store name to reset
   * @returns {Promise<*>} Result from addStorePresets helper
   * @example
   * // Reset activity types to defaults
   * await GeoMoverDB.reAddStoreData('activityTypes')
   */
  async reAddStoreData (store) {
    return await addStorePresets(this.db, store);
  },

  /**
   * Initialize the IndexedDB database
   *
   * Opens the database connection and handles schema migrations via onupgradeneeded.
   * Must be called before any other database operations.
   *
   * @returns {Promise<boolean>} True if initialization successful, false on error
   * @throws {Error} If database is outdated and needs page reload
   * @example
   * // In App.vue or main.js
   * import GeoMoverDB from '@/db/geoMoverDB'
   *
   * async function initApp() {
   *   const dbReady = await GeoMoverDB.init()
   *   if (dbReady) {
   *     // Database is ready, proceed with app initialization
   *   }
   * }
   */
  init () {
    return new Promise((resolve, reject) => {
      let settled = false;
      const safeResolve = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      const safeReject = (error) => {
        if (settled) return;
        settled = true;
        reject(error);
      };

      const finalizeOpen = (db) => {
        this.db = db;

        // In case the db is old, close it and tell the user to update
        // @see https://javascript.info/geoMoverDB and below
        this.db.onversionchange = () => {
          this.db.close();
          safeReject(new Error('Database is outdated, please reload the page.'));
        };

        // ── Data-level migrations (no version bump needed) ───────────────────────
        // These run every open and are idempotent.
        // IMPORTANT: resolve() is called from tx.oncomplete so the app
        // doesn't load map hosts before migrations have finished writing.
        try {
          const tx = this.db.transaction(['mapHosts'], 'readwrite');
          const store = tx.objectStore('mapHosts');
          const cursorReq = store.openCursor();
          cursorReq.onsuccess = () => {
            const cursor = cursorReq.result;
            if (!cursor) return;

            const current = cursor.value;
            const next = { ...current };
            let changed = false;

            // Patch cesiumUrl onto HERE (id:2)
            if (Number(current?.id) === 2) {
              const hereCesiumUrl = 'https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png?style=satellite.day&apiKey={apikey}';
              if (current.cesiumUrl !== hereCesiumUrl) {
                next.cesiumUrl = hereCesiumUrl;
                changed = true;
              }
            }

            // Patch cesiumUrl onto ArcGIS (id:12) — correct URL was wrong in v4 seed
            if (Number(current?.id) === 12) {
              const arcgisCesiumUrl = 'https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token={apikey}';
              if (current.cesiumUrl !== arcgisCesiumUrl) {
                next.cesiumUrl = arcgisCesiumUrl;
                changed = true;
              }
            }

            // Remove outdated OpenMapTiles dev-port self-hosting note from description.
            if (String(current?.slug || '').trim() === 'openmaptiles') {
              const legacyOpenMapTilesText = 'for self-hosting, change the URL to your own TileServer GL instance (note: Geo Mover dev server uses port 8080)';
              const currentDescription = String(current?.description || '');
              if (currentDescription.includes(legacyOpenMapTilesText)) {
                next.description = currentDescription
                  .replace(`; ${legacyOpenMapTilesText}.`, '.')
                  .replace(legacyOpenMapTilesText, '')
                  .trim();
                changed = true;
              }
            }

            // Keep Nominatim API-key free in form UX and migration-upgraded rows.
            if (String(current?.slug || '').trim() === 'nominatim') {
              const currentApiKey = current?.apikey && typeof current.apikey === 'object'
                ? current.apikey
                : {};
              const nextApiKey = {
                ...currentApiKey,
                required: false,
                string: String(currentApiKey.string || '').toLowerCase().includes('not required') ? '' : (currentApiKey.string || ''),
                url: String(currentApiKey.url || '').toLowerCase().includes('no key required')
                  ? ''
                  : (currentApiKey.url || ''),
                text: String(currentApiKey.url || '').toLowerCase().includes('no key required')
                  ? ''
                  : (currentApiKey.text || '')
              };

              if (
                currentApiKey.required !== false
                || currentApiKey.string !== nextApiKey.string
                || currentApiKey.url !== nextApiKey.url
                || currentApiKey.text !== nextApiKey.text
              ) {
                next.apikey = nextApiKey;
                changed = true;
              }
            }

            // Ensure geocoding capability contract fields exist on all mapHosts rows.
            const capabilityDefaults = applyMapHostGeocodeCapabilityDefaults(current);
            for (const key of MAP_HOST_GEOCODE_CAPABILITY_KEYS) {
              if (current[key] !== capabilityDefaults[key]) {
                next[key] = capabilityDefaults[key];
                changed = true;
              }
            }

            if (changed) {
              next.updatedAt = Date.now();
              cursor.update(next);
            }

            cursor.continue();
          };

          cursorReq.onerror = () => {
            appDebugLog.warn('GeoMoverDB', 'Could not scan mapHosts during runtime migration pass.');
          };

          // Only resolve AFTER all migration writes have committed
          tx.oncomplete = () => safeResolve(true);
          tx.onerror = () => {
            appDebugLog.warn('GeoMoverDB', 'Data migration transaction error:', tx.error);
            safeResolve(true); // still resolve — migration failure is non-fatal
          };
        }
        catch (_) {
          // Non-fatal — silently skip if mapHosts store not yet created
          safeResolve(true);
        }
        // ────────────────────────────────────────────────────────────────────────
      };

      this.openDBRequest = indexedDB.open('GeoMoverDB', version);

      this.openDBRequest.onupgradeneeded = (e) => {
        appDebugLog.log('GeoMoverDB', 'Creating/Updating database');
        appDebugLog.log('GeoMoverDB', `oldVersion: ${e.oldVersion}, newVersion: ${e.newVersion}`);

        const db = e.target.result;
        const txn = e.target.transaction;

        // ============================================================
        // STORE: settings (single record with all app settings)
        // ============================================================
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { autoIncrement: true });
          txn.objectStore('settings').add(initialSettingsData);
          appDebugLog.info('GeoMoverDB', 'Created store: settings');
        }

        // ============================================================
        // STORE: activityTypes (Biking, Walking, Hiking, etc.)
        // ============================================================
        if (!db.objectStoreNames.contains('activityTypes')) {
          const store = db.createObjectStore('activityTypes', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('title', 'title', { unique: true });
          initialActivityTypesData.forEach(item => txn.objectStore('activityTypes').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: activityTypes');
        }

        // ============================================================
        // STORE: trackedRoutes (recorded GPS tracks)
        // ============================================================
        if (!db.objectStoreNames.contains('trackedRoutes')) {
          const store = db.createObjectStore('trackedRoutes', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('date', 'date', { unique: true });
          store.createIndex('distanceKm', 'distanceKm', { unique: false });
          store.createIndex('distanceM', 'distanceM', { unique: false });
          store.createIndex('durationStartStop', 'durationStartStop', { unique: false });
          store.createIndex('steps', 'steps', { unique: false });
          store.createIndex('title', 'title', { unique: false });
          store.createIndex('typeTitle', 'typeTitle', { unique: false });
          // multiEntry index for tag-based filtering (added in v2)
          store.createIndex('tagIds', 'tagIds', { unique: false, multiEntry: true });
          initialTrackedRoutesData.forEach(item => txn.objectStore('trackedRoutes').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: trackedRoutes');
        }

        // ============================================================
        // STORE: trackedRouteVersions (saved revision history per track)
        // Added in version 5
        // ============================================================
        if (!db.objectStoreNames.contains('trackedRouteVersions')) {
          const store = db.createObjectStore('trackedRouteVersions', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('trackId', 'trackId', { unique: false });
          store.createIndex('versionNumber', 'versionNumber', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('source', 'source', { unique: false });
          appDebugLog.info('GeoMoverDB', 'Created store: trackedRouteVersions');
        }

        // ============================================================
        // STORE: notes (user notes/annotations)
        // ============================================================
        if (!db.objectStoreNames.contains('notes')) {
          const store = db.createObjectStore('notes', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('title', 'title', { unique: false });
          store.createIndex('text', 'text', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: true });
          // multiEntry index for tag-based filtering (added in v2)
          store.createIndex('tagIds', 'tagIds', { unique: false, multiEntry: true });
          initialNotesData.forEach(item => txn.objectStore('notes').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: notes');
        }

        // ============================================================
        // STORE: poiLists (logical groupings for POIs)
        // Added in version 7
        // ============================================================
        if (!db.objectStoreNames.contains('poiLists')) {
          const store = db.createObjectStore('poiLists', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('title', 'title', { unique: false });
          store.createIndex('isDefault', 'isDefault', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          initialPoiListsData.forEach(item => txn.objectStore('poiLists').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: poiLists');
        }

        // ============================================================
        // STORE: poiTaxonomies (user-managed POI categories)
        // Added in version 7
        // ============================================================
        if (!db.objectStoreNames.contains('poiTaxonomies')) {
          const store = db.createObjectStore('poiTaxonomies', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('title', 'title', { unique: true });
          store.createIndex('iconKey', 'iconKey', { unique: false });
          store.createIndex('colorKey', 'colorKey', { unique: false });
          store.createIndex('colorId', 'colorId', { unique: false });
          store.createIndex('sortOrder', 'sortOrder', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          initialPoiTaxonomiesData.forEach(item => txn.objectStore('poiTaxonomies').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: poiTaxonomies');
        }

        // ============================================================
        // STORE: pois (points of interest)
        // Added in version 7
        // ============================================================
        if (!db.objectStoreNames.contains('pois')) {
          const store = db.createObjectStore('pois', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('title', 'title', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          store.createIndex('listId', 'listId', { unique: false });
          store.createIndex('taxonomyIds', 'taxonomyIds', { unique: false, multiEntry: true });
          store.createIndex('tagIds', 'tagIds', { unique: false, multiEntry: true });
          store.createIndex('source', 'source', { unique: false });
          store.createIndex('searchProviderId', 'searchProviderId', { unique: false });
          appDebugLog.info('GeoMoverDB', 'Created store: pois');
        }

        // ============================================================
        // STORE: poiNoteLinks (m:n POIs <-> notes)
        // Added in version 7, naming finalized in version 8
        // ============================================================
        if (!db.objectStoreNames.contains('poiNoteLinks')) {
          const store = db.createObjectStore('poiNoteLinks', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('noteId', 'noteId', { unique: false });
          store.createIndex('poiId', 'poiId', { unique: false });
          store.createIndex('poiNote', ['poiId', 'noteId'], { unique: true });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          appDebugLog.info('GeoMoverDB', 'Created store: poiNoteLinks');
        }

        // ============================================================
        // STORE: poiRouteLinks (m:n POIs <-> routes)
        // Added in version 7
        // ============================================================
        if (!db.objectStoreNames.contains('poiRouteLinks')) {
          const store = db.createObjectStore('poiRouteLinks', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('poiId', 'poiId', { unique: false });
          store.createIndex('routeId', 'routeId', { unique: false });
          store.createIndex('poiRoute', ['poiId', 'routeId'], { unique: true });
          store.createIndex('distanceMetersAtLinkTime', 'distanceMetersAtLinkTime', { unique: false });
          store.createIndex('thresholdMeters', 'thresholdMeters', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          appDebugLog.info('GeoMoverDB', 'Created store: poiRouteLinks');
        }

        // ============================================================
        // STORE: noteRouteLinks (m:n notes <-> routes)
        // Added in version 9
        // ============================================================
        if (!db.objectStoreNames.contains('noteRouteLinks')) {
          const store = db.createObjectStore('noteRouteLinks', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('noteId', 'noteId', { unique: false });
          store.createIndex('routeId', 'routeId', { unique: false });
          store.createIndex('noteRoute', ['noteId', 'routeId'], { unique: true });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          appDebugLog.info('GeoMoverDB', 'Created store: noteRouteLinks');
        }

        // ============================================================
        // STORE: poiPhotos (normalized POI photo blobs + metadata)
        // Added in version 7
        // ============================================================
        if (!db.objectStoreNames.contains('poiPhotos')) {
          const store = db.createObjectStore('poiPhotos', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('poiId', 'poiId', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          appDebugLog.info('GeoMoverDB', 'Created store: poiPhotos');
        }

        // ============================================================
        // STORE: poiSearchRadiusPresets (editable POI search radius values)
        // Added in version 10
        // ============================================================
        if (!db.objectStoreNames.contains('poiSearchRadiusPresets')) {
          const store = db.createObjectStore('poiSearchRadiusPresets', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('label', 'label', { unique: true });
          store.createIndex('sortOrder', 'sortOrder', { unique: false });
          store.createIndex('km', 'km', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          initialPoiSearchRadiusPresetsData.forEach(item => txn.objectStore('poiSearchRadiusPresets').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: poiSearchRadiusPresets');
        }

        // ============================================================
        // STORE: iconCatalog (shared reusable icon library selections)
        // Added in version 11
        // ============================================================
        if (!db.objectStoreNames.contains('iconCatalog')) {
          const store = db.createObjectStore('iconCatalog', { autoIncrement: true, keyPath: 'id' });
          store.createIndex('iconKey', 'iconKey', { unique: true });
          store.createIndex('label', 'label', { unique: false });
          store.createIndex('library', 'library', { unique: false });
          store.createIndex('category', 'category', { unique: false });
          store.createIndex('isEnabled', 'isEnabled', { unique: false });
          store.createIndex('sortOrder', 'sortOrder', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          initialIconCatalogData.forEach(item => txn.objectStore('iconCatalog').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: iconCatalog');
        }

        // ============================================================
        // STORE: mapHosts (map tile providers)
        // ============================================================
        if (!db.objectStoreNames.contains('mapHosts')) {
          const store = db.createObjectStore('mapHosts', { keyPath: 'id', autoIncrement: true });
          store.createIndex('active', 'active', { unique: false });
          store.createIndex('title', 'title', { unique: true });
          store.createIndex('type', 'type', { unique: false });
          initialMapHostsData.forEach(item => txn.objectStore('mapHosts').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: mapHosts');
        }

        // ============================================================
        // STORE: crash (for recovering interrupted recordings)
        // ============================================================
        if (!db.objectStoreNames.contains('crash')) {
          db.createObjectStore('crash', { autoIncrement: true, keyPath: 'id' });
          appDebugLog.info('GeoMoverDB', 'Created store: crash');
        }

        // ============================================================
        // STORE: overlayServices (weather overlays, etc.)
        // ============================================================
        if (!db.objectStoreNames.contains('overlayServices')) {
          const store = db.createObjectStore('overlayServices', { keyPath: 'id', autoIncrement: true });
          store.createIndex('active', 'active', { unique: false });
          store.createIndex('title', 'title', { unique: true });
          store.createIndex('slug', 'slug', { unique: true });
          store.createIndex('type', 'type', { unique: false });
          initialOverlayServicesData.forEach(item => txn.objectStore('overlayServices').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: overlayServices');
        }

        // ============================================================
        // STORE: routingProviders (BYOK route correction providers)
        // Added in version 3
        // ============================================================
        if (!db.objectStoreNames.contains('routingProviders')) {
          const store = db.createObjectStore('routingProviders', { keyPath: 'id', autoIncrement: true });
          store.createIndex('slug', 'slug', { unique: true });
          store.createIndex('active', 'active', { unique: false });
          store.createIndex('priority', 'priority', { unique: false });
          initialRoutingProvidersData.forEach(item => txn.objectStore('routingProviders').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: routingProviders');
        }

        // ============================================================
        // STORE: overlayState (persisted overlay layer state)
        // ============================================================
        if (!db.objectStoreNames.contains('overlayState')) {
          const store = db.createObjectStore('overlayState', { keyPath: 'id' });
          txn.objectStore('overlayState').add({
            id: 1,
            activeOverlayLayers: [],
            layerOpacities: {},
            lastUpdated: Date.now()
          });
          appDebugLog.info('GeoMoverDB', 'Created store: overlayState');
        }

        // ============================================================
        // STORE: settingsPresets (activity-specific geolocation presets)
        // ============================================================
        if (!db.objectStoreNames.contains('settingsPresets')) {
          const store = db.createObjectStore('settingsPresets', { keyPath: 'id', autoIncrement: true });
          store.createIndex('activityTypeId', 'activityTypeId', { unique: false });
          store.createIndex('isActive', 'isActive', { unique: false });
          store.createIndex('isDefault', 'isDefault', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: true });
          initialSettingsPresetsData.forEach(item => txn.objectStore('settingsPresets').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: settingsPresets');
        }

        // ============================================================
        // STORE: colorPalettes (user-customizable color schemes)
        // ============================================================
        if (!db.objectStoreNames.contains('colorPalettes')) {
          const store = db.createObjectStore('colorPalettes', { keyPath: 'id', autoIncrement: true });
          initialColorPalettesData.forEach(item => txn.objectStore('colorPalettes').add(item));
          appDebugLog.info('GeoMoverDB', 'Created store: colorPalettes');
        }

        // ============================================================
        // STORE: tags (user-defined tags for tracks, notes, POIs)
        // Added in version 2
        // ============================================================
        if (!db.objectStoreNames.contains('tags')) {
          const store = db.createObjectStore('tags', { keyPath: 'id', autoIncrement: true });
          store.createIndex('name', 'name', { unique: true });
          initialTagsData.forEach(item => txn.objectStore('tags').add({
            ...item,
            createdAt: Date.now()
          }));
          appDebugLog.info('GeoMoverDB', 'Created store: tags');
        }

        // ============================================================
        // VERSION 2 UPGRADE: Add tagIds index to existing stores
        // ============================================================
        if (e.oldVersion < 2) {
          // Add tagIds index to trackedRoutes if it exists but doesn't have the index
          if (db.objectStoreNames.contains('trackedRoutes')) {
            const trackedRoutesStore = txn.objectStore('trackedRoutes');
            if (!trackedRoutesStore.indexNames.contains('tagIds')) {
              trackedRoutesStore.createIndex('tagIds', 'tagIds', { unique: false, multiEntry: true });
              appDebugLog.info('GeoMoverDB', 'Added tagIds index to trackedRoutes');
            }
          }
          // Add tagIds index to notes if it exists but doesn't have the index
          if (db.objectStoreNames.contains('notes')) {
            const notesStore = txn.objectStore('notes');
            if (!notesStore.indexNames.contains('tagIds')) {
              notesStore.createIndex('tagIds', 'tagIds', { unique: false, multiEntry: true });
              appDebugLog.info('GeoMoverDB', 'Added tagIds index to notes');
            }
          }
        }

        // ============================================================
        // VERSION 3 UPGRADE: Ensure routingProviders store exists
        // ============================================================
        if (e.oldVersion < 3) {
          if (!db.objectStoreNames.contains('routingProviders')) {
            const routingStore = db.createObjectStore('routingProviders', { keyPath: 'id', autoIncrement: true });
            routingStore.createIndex('slug', 'slug', { unique: true });
            routingStore.createIndex('active', 'active', { unique: false });
            routingStore.createIndex('priority', 'priority', { unique: false });
            initialRoutingProvidersData.forEach(item => txn.objectStore('routingProviders').add(item));
            appDebugLog.info('GeoMoverDB', 'Added routingProviders store (v3 upgrade)');
          }
        }

        // ============================================================
        // VERSION 4 UPGRADE: Add ArcGIS / Esri provider to mapHosts
        // ============================================================
        if (e.oldVersion < 4) {
          if (db.objectStoreNames.contains('mapHosts')) {
            const mapHostsStore = txn.objectStore('mapHosts');
            const arcgisSeed = initialMapHostsData.find(h => h.slug === 'arcgis');
            if (arcgisSeed) {
              const addReq = mapHostsStore.add({ ...arcgisSeed, createdAt: Date.now() });
              addReq.onsuccess = () => {
                appDebugLog.info('GeoMoverDB', 'Added ArcGIS provider to mapHosts (v4 upgrade)');
              };
              // Some older DB states may already contain ArcGIS (same key).
              // Prevent migration abort from duplicate-key collisions.
              addReq.onerror = (event) => {
                event.preventDefault?.();
                appDebugLog.warn('GeoMoverDB', 'Skipping ArcGIS v4 seed add (already exists or key conflict)');
              };
            }
          }
        }

        // ============================================================
        // VERSION 5 UPGRADE: Add trackedRouteVersions store
        // ============================================================
        if (e.oldVersion < 5) {
          if (!db.objectStoreNames.contains('trackedRouteVersions')) {
            const versionsStore = db.createObjectStore('trackedRouteVersions', { autoIncrement: true, keyPath: 'id' });
            versionsStore.createIndex('trackId', 'trackId', { unique: false });
            versionsStore.createIndex('versionNumber', 'versionNumber', { unique: false });
            versionsStore.createIndex('createdAt', 'createdAt', { unique: false });
            versionsStore.createIndex('source', 'source', { unique: false });
            appDebugLog.info('GeoMoverDB', 'Added trackedRouteVersions store (v5 upgrade)');
          }
        }

        // ============================================================
        // VERSION 7 UPGRADE: Add POI stores and indexes
        // ============================================================
        if (e.oldVersion < 7) {
          if (db.objectStoreNames.contains('poiLists')) {
            const poiListsStore = txn.objectStore('poiLists');
            if (!poiListsStore.indexNames.contains('title')) {
              poiListsStore.createIndex('title', 'title', { unique: false });
            }
            if (!poiListsStore.indexNames.contains('isDefault')) {
              poiListsStore.createIndex('isDefault', 'isDefault', { unique: false });
            }
            if (!poiListsStore.indexNames.contains('createdAt')) {
              poiListsStore.createIndex('createdAt', 'createdAt', { unique: false });
            }
            if (!poiListsStore.indexNames.contains('updatedAt')) {
              poiListsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            }
            ensureDefaultPoiListSeed(poiListsStore);
          }

          if (db.objectStoreNames.contains('poiTaxonomies')) {
            const poiTaxonomiesStore = txn.objectStore('poiTaxonomies');
            if (!poiTaxonomiesStore.indexNames.contains('title')) {
              poiTaxonomiesStore.createIndex('title', 'title', { unique: true });
            }
            if (!poiTaxonomiesStore.indexNames.contains('iconKey')) {
              poiTaxonomiesStore.createIndex('iconKey', 'iconKey', { unique: false });
            }
            if (!poiTaxonomiesStore.indexNames.contains('colorKey')) {
              poiTaxonomiesStore.createIndex('colorKey', 'colorKey', { unique: false });
            }
            if (!poiTaxonomiesStore.indexNames.contains('colorId')) {
              poiTaxonomiesStore.createIndex('colorId', 'colorId', { unique: false });
            }
            if (!poiTaxonomiesStore.indexNames.contains('sortOrder')) {
              poiTaxonomiesStore.createIndex('sortOrder', 'sortOrder', { unique: false });
            }
            if (!poiTaxonomiesStore.indexNames.contains('createdAt')) {
              poiTaxonomiesStore.createIndex('createdAt', 'createdAt', { unique: false });
            }
            if (!poiTaxonomiesStore.indexNames.contains('updatedAt')) {
              poiTaxonomiesStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            }
          }

          if (db.objectStoreNames.contains('pois')) {
            const poisStore = txn.objectStore('pois');
            if (!poisStore.indexNames.contains('title')) {
              poisStore.createIndex('title', 'title', { unique: false });
            }
            if (!poisStore.indexNames.contains('updatedAt')) {
              poisStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            }
            if (!poisStore.indexNames.contains('listId')) {
              poisStore.createIndex('listId', 'listId', { unique: false });
            }
            if (!poisStore.indexNames.contains('taxonomyIds')) {
              poisStore.createIndex('taxonomyIds', 'taxonomyIds', { unique: false, multiEntry: true });
            }
            if (!poisStore.indexNames.contains('tagIds')) {
              poisStore.createIndex('tagIds', 'tagIds', { unique: false, multiEntry: true });
            }
            if (!poisStore.indexNames.contains('source')) {
              poisStore.createIndex('source', 'source', { unique: false });
            }
            if (!poisStore.indexNames.contains('searchProviderId')) {
              poisStore.createIndex('searchProviderId', 'searchProviderId', { unique: false });
            }
          }

          if (db.objectStoreNames.contains('poiNoteLinks')) {
            const poiNoteLinksStore = txn.objectStore('poiNoteLinks');
            if (!poiNoteLinksStore.indexNames.contains('noteId')) {
              poiNoteLinksStore.createIndex('noteId', 'noteId', { unique: false });
            }
            if (!poiNoteLinksStore.indexNames.contains('poiId')) {
              poiNoteLinksStore.createIndex('poiId', 'poiId', { unique: false });
            }
            if (!poiNoteLinksStore.indexNames.contains('poiNote')) {
              poiNoteLinksStore.createIndex('poiNote', ['poiId', 'noteId'], { unique: true });
            }
            if (!poiNoteLinksStore.indexNames.contains('createdAt')) {
              poiNoteLinksStore.createIndex('createdAt', 'createdAt', { unique: false });
            }
            if (!poiNoteLinksStore.indexNames.contains('updatedAt')) {
              poiNoteLinksStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            }
          }

          if (db.objectStoreNames.contains('poiRouteLinks')) {
            const poiRouteLinksStore = txn.objectStore('poiRouteLinks');
            if (!poiRouteLinksStore.indexNames.contains('poiId')) {
              poiRouteLinksStore.createIndex('poiId', 'poiId', { unique: false });
            }
            if (!poiRouteLinksStore.indexNames.contains('routeId')) {
              poiRouteLinksStore.createIndex('routeId', 'routeId', { unique: false });
            }
            if (!poiRouteLinksStore.indexNames.contains('poiRoute')) {
              poiRouteLinksStore.createIndex('poiRoute', ['poiId', 'routeId'], { unique: true });
            }
            if (!poiRouteLinksStore.indexNames.contains('distanceMetersAtLinkTime')) {
              poiRouteLinksStore.createIndex('distanceMetersAtLinkTime', 'distanceMetersAtLinkTime', { unique: false });
            }
            if (!poiRouteLinksStore.indexNames.contains('thresholdMeters')) {
              poiRouteLinksStore.createIndex('thresholdMeters', 'thresholdMeters', { unique: false });
            }
            if (!poiRouteLinksStore.indexNames.contains('createdAt')) {
              poiRouteLinksStore.createIndex('createdAt', 'createdAt', { unique: false });
            }
            if (!poiRouteLinksStore.indexNames.contains('updatedAt')) {
              poiRouteLinksStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            }
          }

          if (db.objectStoreNames.contains('poiPhotos')) {
            const poiPhotosStore = txn.objectStore('poiPhotos');
            if (!poiPhotosStore.indexNames.contains('poiId')) {
              poiPhotosStore.createIndex('poiId', 'poiId', { unique: false });
            }
            if (!poiPhotosStore.indexNames.contains('createdAt')) {
              poiPhotosStore.createIndex('createdAt', 'createdAt', { unique: false });
            }
            if (!poiPhotosStore.indexNames.contains('updatedAt')) {
              poiPhotosStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            }
          }
        }

        // ============================================================
        // VERSION 8 UPGRADE: Finalize POI-note link naming (notePoiLinks -> poiNoteLinks)
        // ============================================================
        if (e.oldVersion < 8) {
          if (db.objectStoreNames.contains('poiLists')) {
            ensureDefaultPoiListSeed(txn.objectStore('poiLists'));
          }

          if (!db.objectStoreNames.contains('poiNoteLinks')) {
            const poiNoteLinksStore = db.createObjectStore('poiNoteLinks', { autoIncrement: true, keyPath: 'id' });
            poiNoteLinksStore.createIndex('noteId', 'noteId', { unique: false });
            poiNoteLinksStore.createIndex('poiId', 'poiId', { unique: false });
            poiNoteLinksStore.createIndex('poiNote', ['poiId', 'noteId'], { unique: true });
            poiNoteLinksStore.createIndex('createdAt', 'createdAt', { unique: false });
            poiNoteLinksStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            appDebugLog.info('GeoMoverDB', 'Added poiNoteLinks store (v8 upgrade)');
          }

          // Best-effort migration for local DBs created on early POI branches.
          if (db.objectStoreNames.contains('notePoiLinks') && db.objectStoreNames.contains('poiNoteLinks')) {
            const legacyStore = txn.objectStore('notePoiLinks');
            const targetStore = txn.objectStore('poiNoteLinks');
            appDebugLog.info('GeoMoverDB', 'Migrating legacy notePoiLinks records to poiNoteLinks (v8 upgrade).');

            const cursorReq = legacyStore.openCursor();
            cursorReq.onsuccess = () => {
              const cursor = cursorReq.result;
              if (!cursor) return;

              const putReq = targetStore.put(cursor.value);
              putReq.onerror = (event) => {
                // Non-fatal on duplicate rows or shape mismatch.
                event.preventDefault?.();
                appDebugLog.warn('GeoMoverDB', 'Skipping one legacy notePoiLinks row during v8 migration.');
              };

              cursor.continue();
            };
            cursorReq.onerror = () => {
              appDebugLog.warn('GeoMoverDB', 'Failed reading legacy notePoiLinks during v8 migration.');
            };
          }
        }

        // ============================================================
        // VERSION 9 UPGRADE: Add noteRouteLinks store
        // ============================================================
        if (e.oldVersion < 9) {
          if (!db.objectStoreNames.contains('noteRouteLinks')) {
            const noteRouteLinksStore = db.createObjectStore('noteRouteLinks', { autoIncrement: true, keyPath: 'id' });
            noteRouteLinksStore.createIndex('noteId', 'noteId', { unique: false });
            noteRouteLinksStore.createIndex('routeId', 'routeId', { unique: false });
            noteRouteLinksStore.createIndex('noteRoute', ['noteId', 'routeId'], { unique: true });
            noteRouteLinksStore.createIndex('createdAt', 'createdAt', { unique: false });
            noteRouteLinksStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            appDebugLog.info('GeoMoverDB', 'Added noteRouteLinks store (v9 upgrade)');
          }
        }

        // ============================================================
        // VERSION 10 UPGRADE: Add poiSearchRadiusPresets store
        // ============================================================
        if (e.oldVersion < 10) {
          if (!db.objectStoreNames.contains('poiSearchRadiusPresets')) {
            const presetStore = db.createObjectStore('poiSearchRadiusPresets', { autoIncrement: true, keyPath: 'id' });
            presetStore.createIndex('label', 'label', { unique: true });
            presetStore.createIndex('sortOrder', 'sortOrder', { unique: false });
            presetStore.createIndex('km', 'km', { unique: false });
            presetStore.createIndex('createdAt', 'createdAt', { unique: false });
            presetStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            initialPoiSearchRadiusPresetsData.forEach(item => txn.objectStore('poiSearchRadiusPresets').add(item));
            appDebugLog.info('GeoMoverDB', 'Added poiSearchRadiusPresets store (v10 upgrade)');
          }
        }

        // ============================================================
        // VERSION 11 UPGRADE: Add shared icon catalog + taxonomy ordering/colorId
        // ============================================================
        if (e.oldVersion < 11) {
          if (!db.objectStoreNames.contains('iconCatalog')) {
            const iconCatalogStore = db.createObjectStore('iconCatalog', { autoIncrement: true, keyPath: 'id' });
            iconCatalogStore.createIndex('iconKey', 'iconKey', { unique: true });
            iconCatalogStore.createIndex('label', 'label', { unique: false });
            iconCatalogStore.createIndex('library', 'library', { unique: false });
            iconCatalogStore.createIndex('category', 'category', { unique: false });
            iconCatalogStore.createIndex('isEnabled', 'isEnabled', { unique: false });
            iconCatalogStore.createIndex('sortOrder', 'sortOrder', { unique: false });
            iconCatalogStore.createIndex('createdAt', 'createdAt', { unique: false });
            iconCatalogStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            initialIconCatalogData.forEach(item => txn.objectStore('iconCatalog').add(item));
            appDebugLog.info('GeoMoverDB', 'Added iconCatalog store (v11 upgrade)');
          }

          if (db.objectStoreNames.contains('poiTaxonomies')) {
            const poiTaxonomiesStore = txn.objectStore('poiTaxonomies');
            if (!poiTaxonomiesStore.indexNames.contains('sortOrder')) {
              poiTaxonomiesStore.createIndex('sortOrder', 'sortOrder', { unique: false });
            }
            if (!poiTaxonomiesStore.indexNames.contains('colorId')) {
              poiTaxonomiesStore.createIndex('colorId', 'colorId', { unique: false });
            }
          }
        }

        appDebugLog.success('GeoMoverDB', 'Database schema ready');
      };

      this.openDBRequest.onsuccess = e => {
        finalizeOpen(e.target.result);
      };

      this.openDBRequest.onerror = e => {
        const openError = e?.target?.error ?? e;
        appDebugLog.error('GeoMoverDB', 'Error opening database', openError);

        // Handle rollback/downgrade mismatch gracefully:
        // if a user already has a newer DB version, open latest existing version.
        if (openError?.name === 'VersionError') {
          appDebugLog.warn('GeoMoverDB', 'Version mismatch detected. Retrying open with existing DB version.');
          const fallbackOpenReq = indexedDB.open('GeoMoverDB');

          fallbackOpenReq.onsuccess = event => {
            finalizeOpen(event.target.result);
          };
          fallbackOpenReq.onerror = fallbackErrorEvent => {
            appDebugLog.error('GeoMoverDB', 'Fallback open failed', fallbackErrorEvent?.target?.error ?? fallbackErrorEvent);
            safeResolve(false);
          };
          fallbackOpenReq.onblocked = () => {
            appDebugLog.warn('GeoMoverDB', 'Fallback DB open is blocked by another tab/window.');
            safeResolve(false);
          };
          return;
        }

        safeResolve(false);
      };

      this.openDBRequest.onblocked = function () {
        // this event shouldn't trigger if we handle onversionchange correctly
        // it means that there's another open connection to same database
        // (which could happen, when the app is opened in 2 windows, one with
        // v1, another one with v2 – rarly but could happen in the course
        // of an update process...) and it wasn't closed after db.onversionchange
        // triggered for it
        appDebugLog.warn('GeoMoverDB', 'Database open is blocked by another tab/window. Close other GeoMover instances and reload.');
        safeResolve(false);
      };
    });
  },

  /*******************************************
   * Settings Presets Methods
   *******************************************/

  /**
   * Get all settings presets
   * @returns {Promise<Array<Object>>} Array of all presets
   */
  getAllPresets () {
    return this.findAll('settingsPresets');
  },

  /**
   * Get the currently active preset
   * @returns {Promise<Object|null>} The active preset or null if none
   */
  async getActivePreset () {
    // IndexedDB doesn't handle boolean keys well, so we fetch all and filter
    const allPresets = await this.findAll('settingsPresets');
    return allPresets.find(preset => preset.isActive === true) || null;
  },

  /**
   * Get all presets for a specific activity type
   * @param {number|null} activityTypeId - The activity type ID (null for universal presets)
   * @returns {Promise<Array<Object>>} Array of presets for the activity
   */
  getPresetsByActivityType (activityTypeId) {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(['settingsPresets'], 'readonly');
      const store = tx.objectStore('settingsPresets');
      const index = store.index('activityTypeId');
      const req = index.getAll(activityTypeId);

      req.onsuccess = () => {
        resolve(req.result || []);
      };

      req.onerror = e => {
        reject(new Error('Could not get presets by activity: ' + e.target.error?.message));
      };
    });
  },

  /**
   * Set a preset as the active preset (deactivates all others)
   * @param {number} presetId - The ID of the preset to activate
   * @returns {Promise<boolean>} True if successful
   */
  async setActivePreset (presetId) {
    // First, get all presets and deactivate them
    const allPresets = await this.findAll('settingsPresets');

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(['settingsPresets'], 'readwrite');
      const store = tx.objectStore('settingsPresets');
      let pendingOps = allPresets.length;

      for (const preset of allPresets) {
        const shouldBeActive = preset.id === presetId;
        if (preset.isActive !== shouldBeActive) {
          preset.isActive = shouldBeActive;
          const req = store.put(preset);
          req.onerror = e => {
            appDebugLog.error('Error updating preset active state:', e);
          };
        }
        pendingOps--;
      }

      tx.oncomplete = () => {
        resolve(true);
      };

      tx.onerror = e => {
        reject(new Error('Could not set active preset: ' + e.target.error?.message));
      };
    });
  },

  /**
   * Delete all presets for a specific activity type (cascade delete)
   * @param {number} activityTypeId - The activity type ID
   * @returns {Promise<number>} Number of presets deleted
   */
  async deletePresetsByActivityType (activityTypeId) {
    const presets = await this.getPresetsByActivityType(activityTypeId);

    if (presets.length === 0) {
      return 0;
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(['settingsPresets'], 'readwrite');
      const store = tx.objectStore('settingsPresets');
      let deletedCount = 0;

      for (const preset of presets) {
        const req = store.delete(preset.id);
        req.onsuccess = () => {
          deletedCount++;
        };
        req.onerror = e => {
          appDebugLog.error('Error deleting preset:', e);
        };
      }

      tx.oncomplete = () => {
        resolve(deletedCount);
      };

      tx.onerror = e => {
        reject(new Error('Could not delete presets: ' + e.target.error?.message));
      };
    });
  },

  /**
   * Create a default preset for a new activity type
   * @param {number} activityTypeId - The activity type ID
   * @param {string} activityName - The activity type name (for preset name)
   * @returns {Promise<number>} The new preset ID
   */
  async createDefaultPreset (activityTypeId, activityName) {
    const defaultPreset = {
      presetName: `Default ${activityName}`,
      activityTypeId,
      isActive: false,
      isDefault: true,
      createdAt: Date.now(),
      // Default geolocation settings
      enableHighAccuracy: false,
      maximumAge: 0,
      timeout: 5000,
      useMinSpeedThreshold: true,
      minSpeedThreshold: 0.9,
      useRecordingTimeInterval: true,
      recordingTimeInterval: 2000,
      showNotificationWhenNotMoving: false,
      screenWakeLockAvailability: true,
      useTouchScreenProtection: false
    };

    return this.addOne('settingsPresets', defaultPreset);
  }

};

export default GeoMoverDB;
